/** The shell's model: which screen is up, which overlay sits over it, and the
 *  session underneath both.
 *
 * It is an external store rather than React state for two reasons. The debug
 * surface (I15) has to be able to build a scene and show it without a React
 * event, and the turn loop has to be able to open the battle screen from inside
 * an await. Both are outside React's world; one subscription point is simpler
 * and far easier to reason about than a ref maze.
 *
 * No simulation state lives here. `session` is a handle; every read goes
 * through it.
 */

import type {
  ArmyState, ContentRegistry, GameCommand, GameEvent, GameState, HeroId, HeroState,
  ObjectId, PlayerId, TownId, TownState,
} from "@game/core";
import { entityKeys } from "@game/core";
import type { BattleReport } from "@game/ui";
import { GameSession } from "./store.js";
import { TurnLoop, actorToAct, commandPlayer } from "./loop.js";
import type { PlayerController } from "@game/ai";
import { resolveDifficulty } from "@game/ai";
import { HumanController, difficultyIdFor, makeAiController } from "./controllers.js";
import {
  WorkerAiController, WorkerAiHost, spawnAiWorker, workerAiEnabled,
} from "./workerController.js";

/** Wall-clock milliseconds one computer player-turn may plan for. The loop's
 *  budget and the Worker's are the same number so neither outlives the other. */
const AI_BUDGET_MS = 8000;
import { DEFAULT_OPTIONS, startGame, type GameOptions } from "./newgame.js";
import {
  AUTOSAVE_SLOT, QUICKSAVE_SLOT, downloadSave, hasSave, listSaves, loadFromFile,
  loadFromSlot, rememberOptions, rememberedOptions, saveToSlot, type SlotInfo,
} from "./persist.js";

export type Screen =
  | { readonly kind: "loading" }
  | { readonly kind: "menu" }
  | { readonly kind: "newgame" }
  | { readonly kind: "adventure" }
  | { readonly kind: "editor" }
  | { readonly kind: "campaign" };

export type Overlay =
  | { readonly kind: "none" }
  | { readonly kind: "town"; readonly town: TownId }
  /** The hero sheet. `with` is the second hero of a meeting: two of the
   *  player's own heroes standing next to each other, which in the classic is the
   *  exchange window. The sheet is `hero`'s and `with` is the partner it
   *  trades units and artifacts with. */
  | { readonly kind: "hero"; readonly hero: HeroId; readonly with?: HeroId }
  | { readonly kind: "spellbook"; readonly hero: HeroId }
  | { readonly kind: "kingdom" }
  | { readonly kind: "journal" }
  /** The obelisks' picture of where the Grail is buried. */
  | { readonly kind: "puzzle" }
  /** A map object that sells artifacts, opened by clicking it from next to it.
   *  The buyer travels with the overlay: `BUY_ARTIFACT` names a hero, and the
   *  selection can move under an open screen. */
  | { readonly kind: "merchant"; readonly object: ObjectId; readonly hero: HeroId }
  | { readonly kind: "battle" }
  | { readonly kind: "result"; readonly report: BattleReport };

export const NO_OVERLAY: Overlay = Object.freeze({ kind: "none" });

/** A screen built from a fixture rather than from the live session. The debug
 *  surface uses it so a critic can review a screen that a real game has not
 *  reached yet. */
export interface FixtureScreen {
  readonly kind: "town" | "hero" | "spellbook" | "battle" | "menu";
  readonly faction?: string;
  readonly town?: { readonly town: TownState; readonly garrison: ArmyState | null };
  readonly hero?: { readonly hero: HeroState; readonly army: ArmyState | null };
  /** Show the hero sheet as one half of a meeting - the other hero's army row
   *  and the "give to" buttons - so the exchange can be reviewed without a
   *  game that has two heroes standing together. */
  readonly meet?: boolean;
  readonly battle?: {
    readonly siege: boolean;
    readonly terrain?: string;
    /** Override the attacking faction, for scenes that need a given creature. */
    readonly attacker?: string;
  };
}

export interface AppSnapshot {
  readonly revision: number;
  readonly screen: Screen;
  readonly overlay: Overlay;
  readonly fixture: FixtureScreen | null;
  readonly content: ContentRegistry | null;
  readonly session: GameSession | null;
  readonly options: GameOptions;
  readonly error: string | null;
  /** Whose move it is; the shell greys itself while a computer is thinking. */
  readonly actor: PlayerId | null;
  readonly actorKind: "human" | "ai" | "neutral" | "none";
  /**
   * The seat waiting to be taken in a hotseat game, or null.
   *
   * On the snapshot rather than read through a method because the shell has to
   * re-render when it changes: the hand-off screen covers everything, and a
   * screen that only appeared on the next unrelated render would show the
   * incoming player the outgoing player's map in the meantime.
   */
  readonly handoff: PlayerId | null;
  readonly animationsOn: boolean;
  /** Quick Combat: the computer is fighting this machine's battles. */
  readonly autoBattle: boolean;
}

type Listener = () => void;

class AppModel {
  private revision = 1;
  private screen: Screen = { kind: "loading" };
  private overlay: Overlay = NO_OVERLAY;
  private fixture: FixtureScreen | null = null;
  private content: ContentRegistry | null = null;
  private session: GameSession | null = null;
  private options: GameOptions = DEFAULT_OPTIONS;
  private error: string | null = null;
  private actor: PlayerId | null = null;
  private actorKind: AppSnapshot["actorKind"] = "none";
  private animationsOn = true;

  private snapshot: AppSnapshot = this.build();
  private readonly listeners = new Set<Listener>();

  /**
   * The controller for each person at this machine, by player id.
   *
   * One per human seat rather than one for the game. The single-controller
   * version was the whole of what made this build one-player: the kernel has
   * always taken a `kind` per player and `newGame` never counted how many were
   * `human`, but the host had exactly one place to park a command and exactly
   * one viewer to show a map to.
   */
  private readonly humans = new Map<string, HumanController>();
  loop: TurnLoop | null = null;

  /** The AI Worker for the running game (D7), or null when the computer plans
   *  on the main thread - no `Worker` here, or forced off (`workerAiEnabled`). */
  private workerHost: WorkerAiHost | null = null;

  /** How many times the Worker desync check has fired this game. The M3.5
   *  gate is that this stays 0; the debug surface reads it. */
  get aiWorkerDesyncs(): number { return this.workerHost?.desyncs ?? 0; }

  /** Whether the running game's computer seats plan in the Worker. */
  get aiWorkerActive(): boolean { return this.workerHost?.alive === true; }

  /**
   * The shell's menu dispatcher, published for the debug surface only.
   *
   * Some screens are not screens and not overlays: the scoreboard, the settings
   * sheet and the load sheet are React panel state private to `App`, opened by
   * a menu action and by nothing else. `setOverlay` cannot reach them, so
   * before this existed `showScene("scores")` built a game, opened nothing, and
   * photographed the adventure map - a scene the critics had been reviewing as
   * if it were the scoreboard. The bridge is one function reference rather than
   * a second panel API because the panel state has exactly one owner and should
   * keep it.
   *
   * Set by `App` under `import.meta.env.DEV` and read by `debug.ts` (I15).
   */
  debugMenu: ((action: string) => void) | null = null;

  /**
   * Close whatever panel `debugMenu` opened. Published beside it, for the same
   * reason and under the same guard.
   *
   * A panel is not cleared by `reset` or by `go`, because it is React state and
   * neither of those touches it. So the scene that opens the scoreboard leaves
   * it open over every scene photographed afterwards, and the next scene draws
   * *underneath* it - which is silent, because the shot is a real render of a
   * real screen and only the wrong one. It cost another agent a whole capture
   * run before anyone noticed.
   */
  debugClosePanel: (() => void) | null = null;

  /**
   * The seat waiting to be taken, or null.
   *
   * Set when the turn passes from one person to another and cleared when the
   * new one says he is looking. Without it, a hotseat game shows the next
   * player the previous player's map for as long as it takes him to notice,
   * which is the whole of what fog is for.
   */
  private handoff: PlayerId | null = null;
  /** Who last acknowledged a hand-off; a turn returning to them needs none. */
  private seated: PlayerId | null = null;

  /**
   * The controller for whoever the loop last said was acting, when that is a
   * person. Published for the debug surface and the QA harnesses.
   *
   * `dispatch` deliberately does NOT use this. It reads the same mirror the
   * shell greys itself on, and the mirror lags the kernel by however long the
   * loop spends awaiting `present`; routing a command on it is the defect
   * `seatFor` exists to fix.
   */
  get human(): HumanController | null {
    const id = this.actor;
    return id === null ? null : this.humans.get(id as string) ?? null;
  }

  /** Every seat a person is playing, in turn order. */
  humanSeats(): readonly PlayerId[] {
    const state = this.session?.getState();
    if (state === undefined) return [];
    return state.turn.order.filter((id) => this.humans.has(id as string));
  }

  /** The seat waiting to be taken, for the hand-off screen. */
  pendingHandoff(): PlayerId | null { return this.handoff; }

  /** "I am looking": clears the hand-off and shows this player his own map. */
  takeSeat(): void {
    const id = this.handoff;
    if (id === null) return;
    this.handoff = null;
    this.seated = id;
    this.session?.setViewer(id);
    this.emit();
  }

  /** The mounted world presenter, for the debug surface's `state()`. Nothing in
   *  the game loop reads it; the shell talks to its own ref. */
  world: { readonly camera: unknown } | null = null;

  /** Set by the shell so the loop can wait on the presenter's animation. */
  present: ((events: readonly GameEvent[]) => Promise<void>) | null = null;

  /**
   * "Nothing is left to draw and no texture is still loading" (I15) is a claim
   * only the thing holding the GPU can make. Each mounted presenter registers a
   * settler; `settleAll` is what `__GAME_DEBUG__.settle()` waits on. Without it
   * a screenshot lands while a 13-sheet atlas is still decoding and a critic
   * files a missing-sprite bug that is not real.
   */
  private readonly settlers = new Map<string, () => Promise<void>>();

  addSettler(key: string, fn: () => Promise<void>): () => void {
    this.settlers.set(key, fn);
    return () => { if (this.settlers.get(key) === fn) this.settlers.delete(key); };
  }

  async settleAll(): Promise<void> {
    await Promise.all([...this.settlers.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(async ([, fn]) => { await fn(); }));
  }

  // -------------------------------------------------------------- store

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => { this.listeners.delete(l); };
  };

  getSnapshot = (): AppSnapshot => this.snapshot;

  private build(): AppSnapshot {
    return {
      revision: this.revision,
      screen: this.screen,
      overlay: this.overlay,
      fixture: this.fixture,
      content: this.content,
      session: this.session,
      options: this.options,
      error: this.error,
      actor: this.actor,
      actorKind: this.actorKind,
      handoff: this.handoff,
      animationsOn: this.animationsOn,
      autoBattle: this.auto,
    };
  }

  private emit(): void {
    this.revision += 1;
    this.snapshot = this.build();
    for (const l of this.listeners) l();
  }

  // ------------------------------------------------------------ lifecycle

  setContent(content: ContentRegistry): void {
    this.content = content;
    if (this.screen.kind === "loading") this.screen = { kind: "menu" };
    this.emit();
  }

  setError(message: string): void { this.error = message; this.emit(); }

  go(screen: Screen): void {
    this.screen = screen;
    this.overlay = NO_OVERLAY;
    this.fixture = null;
    this.emit();
  }

  setOverlay(overlay: Overlay): void { this.overlay = overlay; this.emit(); }
  closeOverlay(): void { this.overlay = NO_OVERLAY; this.emit(); }
  showFixture(f: FixtureScreen | null): void { this.fixture = f; this.emit(); }

  setAnimationsEnabled(on: boolean): void { this.animationsOn = on; this.emit(); }

  /** Tear down any running game. Safe to call twice. */
  reset(): void {
    this.loop?.stop();
    this.loop = null;
    this.workerHost?.dispose();
    this.workerHost = null;
    this.humans.clear();
    this.handoff = null;
    this.seated = null;
    this.session = null;
    this.actor = null;
    this.actorKind = "none";
  }

  /** Build a game and start its loop. Returns the session so a caller that
   *  needs to poke at it (the debug surface) does not have to re-read. */
  newGame(options: GameOptions): GameSession {
    const content = this.content;
    if (content === null) throw new Error("content is not loaded yet");
    const started = startGame(content, options);
    // Remembered before the game starts rather than after it ends, so a setup
    // survives a tab that is closed mid-game as well as one played to the end.
    rememberOptions(options);
    return this.begin(started.state, started.human, options);
  }

  /**
   * Resume a loaded state.
   *
   * Who is playing comes out of the state, not out of the file: `PlayerState`
   * already says `human` or `ai`, so a save needs no host block to describe its
   * players - which is one fewer place for presentation state to hide (I11).
   *
   * The difficulty is not in the save, because it is not a game fact: it tunes
   * the computer's planner and hands the human his opening purse, and neither
   * is recoverable from a `GameState` on day forty. It is kept *beside* the
   * file instead - in the slot's index row, and as the remembered setup - so
   * the file stays a `GameState` and nothing else while the level still
   * survives closing the tab. See `SlotInfo.options`.
   */
  resumeGame(state: GameState, options?: GameOptions): GameSession {
    const content = this.content;
    if (content === null) throw new Error("content is not loaded yet");
    const players = entityKeys(state.players);
    const human = players.find((id) => state.players[id]?.kind === "human") ?? players[0];
    if (human === undefined) throw new Error("this save has no players");
    return this.begin(state, human, options ?? this.resumeOptions());
  }

  /**
   * The setup to resume a save on when the save did not carry one.
   *
   * Falling back to `this.options` alone was the defect: in a fresh tab that is
   * `DEFAULT_OPTIONS`, so a player who had set Impossible, saved, and come back
   * the next day was handed a Normal opponent without being told. The last
   * setup a game was started on is remembered in local storage, so the fresh
   * tab now resumes on the level the player actually chose.
   */
  private resumeOptions(): GameOptions {
    return { ...this.options, ...(rememberedOptions() ?? {}) };
  }

  /**
   * Quick Combat: is this seat's fighting being done by the computer?
   *
   * Held on the model rather than in the battle screen because the loop can ask
   * for a command before the screen has mounted - a fight begun by walking into
   * a monster starts resolving in the same tick - and because a setting that
   * survives one battle has to live outside it.
   */
  private auto = false;

  get autoBattle(): boolean { return this.auto; }

  /**
   * Turn Quick Combat on or off for every human seat at this machine.
   *
   * Every seat, not just the viewer's: at a hot-seat table the setting belongs
   * to the people playing, and a rule that auto-fought one chair and not the
   * other would be a difference nobody asked for.
   */
  setAutoBattle(on: boolean): void {
    if (this.auto === on) return;
    this.auto = on;
    const content = this.content;
    const options = this.options;
    const auto = !on || content === null || options === null
      ? null
      : { content, difficulty: resolveDifficulty(difficultyIdFor(options.difficulty)) };
    for (const c of this.humans.values()) c.setAutoBattle(auto);
    this.emit();
  }

  private begin(state: GameState, human: PlayerId, options: GameOptions): GameSession {
    const content = this.content;
    if (content === null) throw new Error("content is not loaded yet");
    this.reset();

    const session = new GameSession(state, content, human);
    const difficulty = difficultyIdFor(options.difficulty);
    // One Worker per game, shared by every computer seat: it is a replay
    // client of the one session, and two Workers would be two replays. Built
    // before the loop so the first plan finds it initialised, and only where
    // there is a Worker to build; the main-thread controller stays as the
    // per-turn fallback and as what the headless runner uses.
    const host = workerAiEnabled()
      ? new WorkerAiHost(spawnAiWorker(), session, { setup: { difficulty }, budgetMs: AI_BUDGET_MS })
      : null;
    this.workerHost = host;
    const controllers: Record<string, PlayerController> = {};
    for (const id of entityKeys(state.players)) {
      const p = state.players[id];
      if (p?.kind === "human") {
        const c = new HumanController();
        if (this.auto) {
          c.setAutoBattle({
            content, difficulty: resolveDifficulty(difficulty),
          });
        }
        this.humans.set(id as string, c);
        controllers[id as string] = c;
      } else {
        const local = makeAiController({ content, difficulty });
        controllers[id as string] = host === null ? local : new WorkerAiController(host, local);
      }
    }
    // Whoever the state says is a person; `human` above is only the seat the
    // map opens on, which is the first one in turn order.
    this.seated = human;

    const loop = new TurnLoop(session, {
      content,
      controllers,
      aiBudgetMs: AI_BUDGET_MS,
      present: async (events) => {
        const play = this.present;
        if (play !== null) await play(events);
      },
      onActor: (actor, kind) => {
        if (this.actor === actor && this.actorKind === kind) return;
        this.actor = actor;
        this.actorKind = kind;
        this.followActor(actor, kind);
        this.emit();
      },
      onStop: (reason) => {
        // A loop that stops on its own is either the end of the game or a hang,
        // and a hang that shows as a frozen screen with no message costs the
        // next person an hour. Say which.
        if (reason === "stalled") {
          this.setError(
            "The turn loop stopped making progress. This is usually a battle "
            + "neither side can finish; see the report on AI-vs-neutral combat.",
          );
        }
        this.emit();
      },
    });

    this.session = session;
    this.loop = loop;
    this.options = options;
    this.screen = { kind: "adventure" };
    this.overlay = NO_OVERLAY;
    this.fixture = null;
    this.error = null;
    this.emit();
    loop.start();
    return session;
  }

  // ------------------------------------------------------------ persistence

  /**
   * Save the live state to a browser slot.
   *
   * What is handed to the format is `session.getState()` and nothing else. The
   * camera, the open town screen, the selected hero and the animation queue are
   * all held here or in the presenter, and none of them is reachable from a
   * `GameState`, so I11 holds by construction rather than by discipline.
   */
  saveToBrowser(slot = AUTOSAVE_SLOT): string | null {
    const session = this.session;
    if (session === null) return "There is no game to save.";
    const r = saveToSlot(session.getState(), slot, this.options);
    if (!r.ok) { this.setError(r.message); return r.message; }
    this.emit();
    return null;
  }

  /** F5. A named slot of its own, so the next autosave cannot overwrite it. */
  quickSave(): string | null { return this.saveToBrowser(QUICKSAVE_SLOT); }

  /** F9. Refuses rather than starting a game when there is nothing to reload. */
  quickLoad(): string | null {
    if (!hasSave(QUICKSAVE_SLOT)) return "There is no quicksave to load.";
    return this.loadFromBrowser(QUICKSAVE_SLOT);
  }

  hasQuickSave(): boolean { return hasSave(QUICKSAVE_SLOT); }

  /** Load a slot and start playing it. Returns an error message, or null. */
  loadFromBrowser(slot = AUTOSAVE_SLOT): string | null {
    const content = this.content;
    if (content === null) return "The content bundle is still loading.";
    const r = loadFromSlot(slot, content);
    if (!r.ok) { this.setError(r.message); return r.message; }
    this.resumeGame(r.state, r.options);
    return null;
  }

  /** Load a file the player picked or dropped. */
  async loadFromDisk(file: Blob): Promise<string | null> {
    const content = this.content;
    if (content === null) return "The content bundle is still loading.";
    const r = await loadFromFile(file, content);
    if (!r.ok) { this.setError(r.message); return r.message; }
    this.resumeGame(r.state);
    return null;
  }

  /** Hand the live game to the browser as a download. */
  saveToDisk(): string | null {
    const session = this.session;
    if (session === null) return "There is no game to save.";
    downloadSave(session.getState());
    return null;
  }

  savedGames(): readonly SlotInfo[] { return listSaves(); }
  hasSavedGame(slot = AUTOSAVE_SLOT): boolean { return hasSave(slot); }

  // -------------------------------------------------------------- commands

  /**
   * The UI's only way to change anything. When the loop is parked on the human
   * the command is handed to the controller, so it reaches `executeCommand`
   * down the same pipe the AI uses (I9). If the loop is not running - a fixture
   * screen, or a game that has ended - it goes straight to the session, which
   * is still the one authority.
   */
  dispatch(command: GameCommand): void {
    const seat = this.seatFor(command);
    if (seat !== null && this.loop?.active === true) { seat.offer(command); return; }
    this.session?.execute(command);
    this.emit();
  }

  /**
   * The seat a command should be handed to, or null to run it here.
   *
   * Two questions, and answering the second with the first was the defect.
   *
   * **Who may act** is `actorToAct` on the LIVE state, not `this.actor`.
   * `this.actor` is a mirror the loop refreshes at the top of each iteration,
   * so it is stale for as long as the loop spends awaiting `present` - and the
   * kernel has already handed the turn back by then. In a battle that window
   * opens on every computer blow, because the loop sets `onActor(null,
   * "neutral")` before the monster acts and does not name the human again until
   * after the animation. A command arriving in it found no seat and was run
   * here instead, which skips the turn loop and therefore skips `present`
   * entirely: measured on a real 1600x900 battle, one order in nine, whose
   * events reached the animator a whole batch late and drew the walk as a
   * teleport. The adventure map has the same window between the last computer's
   * turn and the human's - measured at 26-474 ms, three times in three turns -
   * where a move both teleported AND replayed its walk 5.5 s later, when the
   * next command finally drained the queue.
   *
   * **Whose command it is** is `commandPlayer`, and it is a guard rather than
   * the routing key. A seat may never be handed another player's order (I9), so
   * a command whose owner is somebody other than the player the kernel is
   * waiting on is NOT parked in the acting seat: it goes to the session, which
   * refuses it and puts the reason in the status bar exactly as before. A
   * command the state cannot attribute - the owner is gone, the prompt is stale
   * - falls through to the acting seat, which is where it went before this
   * existed.
   *
   * One behaviour changes deliberately. A click made while the kernel really is
   * on the side nobody owns - the wandering monster's own stack, mid-blow - used
   * to be buffered in the human's seat off the stale mirror and replayed when
   * his stack next came up, against a board that had moved on since he aimed at
   * it. It is now refused at once with the kernel's own reason, which is what
   * the status bar is for.
   *
   * Offering to a seat the loop is not parked on yet is safe and is the point:
   * `HumanController.offer` buffers, and the loop takes the buffered command on
   * its next `chooseCommand`. That is what keeps the pacing - the order is
   * executed by the loop, between `present` calls, rather than overtaking the
   * animation the player is watching.
   */
  private seatFor(command: GameCommand): HumanController | null {
    const session = this.session;
    if (session === null) return null;
    const state = session.getState();
    const acting = actorToAct(state);
    if (acting === null) return null;
    const seat = this.humans.get(acting as string);
    if (seat === undefined) return null;
    const owner = commandPlayer(state, command);
    if (owner !== null && owner !== acting) return null;
    return seat;
  }

  /**
   * Point the map at whoever is playing.
   *
   * Two things happen here and they are separate. The **viewer** follows every
   * human actor, so the observation the shell reads is that player's own and
   * the fog is his. The **hand-off** only appears when the seat changes to a
   * different person, and only in a game with more than one: a one-player game
   * must never see it, and a turn coming back round to the same person after
   * the computers have moved is not a hand-off either.
   */
  private followActor(actor: PlayerId | null, kind: AppSnapshot["actorKind"]): void {
    if (actor === null || kind !== "human") return;
    if (!this.humans.has(actor as string)) return;
    if (this.humans.size <= 1) { this.session?.setViewer(actor); this.seated = actor; return; }
    if (this.seated === actor) { this.session?.setViewer(actor); return; }
    // The map stays on the previous player until the next one says he is
    // looking; `takeSeat` is what moves it.
    this.handoff = actor;
  }
}

export const app = new AppModel();
